import { useState, useEffect } from 'react';
import { fetchFacility, fetchSports } from '@/lib/api';
import { MapPin, Phone, Mail, Clock, CircleDot, Target, Volleyball, Zap, Trophy, Navigation } from 'lucide-react';
import SEO from '@/components/SEO';

const SPORT_ICONS = { cricket: CircleDot, baseball: Target, softball: Target, 'soccer-futsal': Volleyball, pickleball: Zap, 'box-cricket': Trophy };
const MAPS_EMBED = "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3236.5!2d-78.8503!3d35.7327!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x89ac8f4b88e0e1a7%3A0x0!2s810+Center+St%2C+Apex%2C+NC+27502!5e0!3m2!1sen!2sus!4v1700000000000";
const MAPS_LINK = "https://www.google.com/maps/dir/?api=1&destination=810+Center+St+Apex+NC+27502";

export default function Facility() {
  const [facility, setFacility] = useState(null);
  const [sports, setSports] = useState([]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchFacility().then(setFacility).catch(() => {});
    fetchSports().then(setSports).catch(() => {});
  }, []);

  if (!facility) return (
    <div className="pt-[68px]">
      <SEO title="Facility" description="Visit Propel Sports NC — 810 Center St, Building #4, Apex NC, 27502. 6 batting tunnels, indoor courts, open 7 days." path="/facility" />
      <section className="relative py-24 bg-[#0B1D3A] overflow-hidden">
        <div className="relative z-10 max-w-7xl mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-6xl font-bold text-white mb-4">Propel Sports NC</h1>
          <p className="text-[#F26522] text-lg font-medium mb-4">Apex's Indoor Sports Home</p>
        </div>
      </section>
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 text-center">
          <div className="flex items-center justify-center gap-2 text-gray-600 mb-6">
            <MapPin size={18} className="text-[#F26522]" />
            <span>810 Center St, Building #4, Apex NC, 27502</span>
          </div>
          <a href={MAPS_LINK} target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-[#F26522] hover:bg-[#D9551A] text-white px-6 py-3 rounded-lg font-semibold text-sm transition-all">
            <Navigation size={15} /> Get Directions
          </a>
          <div data-testid="facility-maps-embed" className="mt-8 rounded-2xl overflow-hidden shadow-md border border-gray-200">
            <iframe src={MAPS_EMBED} width="100%" height="400" style={{ border: 0 }} allowFullScreen loading="lazy"
              referrerPolicy="no-referrer-when-downgrade" title="Propel Sports NC - 810 Center St, Building #4, Apex NC, 27502" />
          </div>
        </div>
      </section>
    </div>
  );

  return (
    <div data-testid="facility-page" className="pt-[68px]">
      <SEO title="Facility" description="Visit Propel Sports NC — 810 Center St, Building #4, Apex NC. 6 batting tunnels, indoor courts, open 7 days a week." path="/facility" />
      <section className="relative py-24 bg-[#0B1D3A] overflow-hidden">
        <div className="absolute inset-0 opacity-20" style={{
          backgroundImage: 'radial-gradient(ellipse 600px 400px at 40% 50%, rgba(242,101,34,0.25) 0%, transparent 70%)'
        }} />
        <div className="relative z-10 max-w-7xl mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-6xl font-bold text-white mb-4">{facility.name}</h1>
          <p className="text-[#F26522] text-lg font-medium mb-4">{facility.tagline}</p>
          <p className="text-gray-300 max-w-2xl mx-auto">{facility.description}</p>
        </div>
      </section>

      {/* Stats */}
      {facility.facility_stats && (
        <section className="py-10 bg-[#F26522]">
          <div className="max-w-5xl mx-auto px-4 grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            {Object.entries(facility.facility_stats).map(([k, v]) => (
              <div key={k}>
                <div className="text-white text-2xl font-bold ">{v}</div>
                <div className="text-white/70 text-sm capitalize">{k}</div>
              </div>
            ))}
          </div>
        </section>
      )}

      <section className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-4 grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Contact Info */}
          <div>
            <h2 className="text-2xl font-bold text-[#0B1D3A] mb-6">Contact Information</h2>
            <div className="space-y-4">
              <div className="flex items-start gap-3 bg-gray-50 border border-gray-200 rounded-xl p-4">
                <MapPin size={20} className="text-[#F26522] mt-0.5" />
                <div>
                  <div className="text-[#0B1D3A] font-medium text-sm">Address</div>
                  <div className="text-gray-500 text-sm">810 Center St, Building #4, Apex NC, 27502</div>
                </div>
              </div>
              <div className="flex items-start gap-3 bg-gray-50 border border-gray-200 rounded-xl p-4">
                <Phone size={20} className="text-[#F26522] mt-0.5" />
                <div>
                  <div className="text-[#0B1D3A] font-medium text-sm">Phone</div>
                  <a href={`tel:${facility.phone}`} className="text-gray-500 text-sm hover:text-[#F26522] transition-colors">{facility.phone}</a>
                </div>
              </div>
              <div className="flex items-start gap-3 bg-gray-50 border border-gray-200 rounded-xl p-4">
                <Mail size={20} className="text-[#F26522] mt-0.5" />
                <div>
                  <div className="text-[#0B1D3A] font-medium text-sm">Email</div>
                  <a href={`mailto:${facility.email}`} className="text-gray-500 text-sm hover:text-[#F26522] transition-colors">{facility.email}</a>
                </div>
              </div>
            </div>
          </div>

          {/* Hours */}
          <div>
            <h2 className="text-2xl font-bold text-[#0B1D3A] mb-6 flex items-center gap-2"><Clock size={20} className="text-[#F26522]" /> Hours of Operation</h2>
            <div className="bg-gray-50 border border-gray-200 rounded-xl p-6 space-y-3">
              {Object.entries(facility.hours || {}).map(([day, time]) => (
                <div key={day} className="flex justify-between items-center py-1 border-b border-gray-100 last:border-0">
                  <span className="text-[#0B1D3A] font-medium text-sm capitalize">{day}</span>
                  <span className="text-gray-500 text-sm">{time}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Sports */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-5xl mx-auto px-4">
          <h2 className="text-2xl font-bold text-[#0B1D3A] mb-8 text-center">Sports We Offer</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {sports.map(sport => {
              const Icon = SPORT_ICONS[sport.id] || CircleDot;
              return (
                <div key={sport.id} data-testid={`facility-sport-${sport.id}`} className="bg-white border border-gray-200 rounded-xl p-5 hover:border-[#F26522]/30 hover:shadow-lg transition-all">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-10 h-10 rounded-lg bg-[#F26522]/10 flex items-center justify-center">
                      <Icon size={20} className="text-[#F26522]" />
                    </div>
                    <h3 className="text-[#0B1D3A] font-bold">{sport.name}</h3>
                  </div>
                  <p className="text-gray-500 text-sm">{sport.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Google Maps */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4">
          <div className="flex flex-col sm:flex-row items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-[#0B1D3A]">Find Us</h2>
            <a href={MAPS_LINK} target="_blank" rel="noopener noreferrer" data-testid="facility-directions-btn"
              className="inline-flex items-center gap-2 bg-[#F26522] hover:bg-[#D9551A] text-white px-5 py-2.5 rounded-lg font-semibold text-sm transition-all mt-3 sm:mt-0">
              <Navigation size={15} /> Get Directions
            </a>
          </div>
          <div data-testid="facility-maps-embed" className="rounded-2xl overflow-hidden shadow-md border border-gray-200">
            <iframe src={MAPS_EMBED} width="100%" height="400" style={{ border: 0 }} allowFullScreen loading="lazy"
              referrerPolicy="no-referrer-when-downgrade" title="Propel Sports NC - 810 Center St, Building #4, Apex NC, 27502" />
          </div>
          <p className="text-center text-gray-500 text-sm mt-4">810 Center St, Building #4, Apex NC, 27502</p>
        </div>
      </section>
    </div>
  );
}
